import { NavLink } from "react-router-dom";
import { CalendarDays, BarChart3, LineChart, Pill, Settings as SettingsIcon, Check, Eye } from "lucide-react";import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProfile } from "@/context/ProfileContext";

const TABS = [
  { to: "/day", label: "Day", icon: CalendarDays, testid: "tab-day" },
  { to: "/compliance", label: "Compliance", icon: BarChart3, testid: "tab-compliance" },
  { to: "/insights", label: "Insights", icon: LineChart, testid: "tab-insights" },
  { to: "/medicines", label: "Medicines", icon: Pill, testid: "tab-medicines" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "tab-settings" },
];

const Avatar = ({ profile, size = "h-9 w-9" }) => {
  const initial = (profile?.name || profile?.email || "?").trim().charAt(0).toUpperCase();
  return profile?.picture ? (
    <img src={profile.picture} alt="" className={`${size} rounded-full object-cover`} />
  ) : (
    <span
      className={`${size} flex items-center justify-center rounded-full bg-sky-500/20 text-sm font-semibold text-sky-200`}
    >
      {initial}
    </span>
  );
};

const ProfileSwitcher = () => {
  const { profiles, active, switchProfile } = useProfile();
  const multi = profiles.length > 1;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="profile-switcher-btn"
          className="flex items-center gap-2 rounded-full pl-0.5 transition-opacity duration-200 active:opacity-70"
        >
          <Avatar profile={active} size="h-8 w-8" />
          {multi && !active?.is_self && (
            <span className="max-w-[80px] truncate text-sm font-medium text-slate-300">
              {active?.name?.split(" ")[0]}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-60 border-slate-800 bg-slate-950/95 backdrop-blur-xl"
        data-testid="profile-menu"
      >
        {profiles.map((p) => (
          <DropdownMenuItem
            key={p.user_id}
            data-testid={`profile-option-${p.user_id}`}
            onClick={() => p.user_id !== active?.user_id && switchProfile(p.user_id)}
            className="flex cursor-pointer items-center gap-2.5 py-2.5"
          >
            <Avatar profile={p} size="h-7 w-7" />
            <span className="min-w-0 flex-1 truncate text-[15px]">
              {p.is_self ? "You" : p.name || p.email}
            </span>
            {p.access === "view" && <Eye className="h-4 w-4 text-amber-400" />}
            {p.user_id === active?.user_id && <Check className="h-4 w-4 text-sky-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const AppFrame = ({ title, header, children }) => {
  const { active } = useProfile();
  const viewOnly = active && !active.is_self && active.access === "view";

  return (
    <div className="grain relative min-h-screen bg-[#0B1120] text-slate-100">
      <div className="aurora" />
      <div className="relative z-10 mx-auto min-h-screen max-w-md overflow-x-hidden pb-32">
        <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-slate-800 bg-slate-950/80 px-4 py-1.5 backdrop-blur-xl">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-sky-500/30 bg-sky-500/10">
            <Pill className="h-4 w-4 text-sky-300" />
          </span>
          <span className="shrink-0 text-[15px] font-bold tracking-tight text-slate-100" data-testid="app-name">
            Pillbox
          </span>
          <div className="pointer-events-none absolute left-1/2 flex max-w-[190px] -translate-x-1/2 items-center justify-center">
            <div className="pointer-events-auto min-w-0">
              {header || (
                <h1
                  className="truncate text-[16px] font-semibold tracking-tight text-slate-100"
                  data-testid="page-title"
                >
                  {title}
                </h1>
              )}
            </div>
          </div>
          <div className="ml-auto shrink-0">
            <ProfileSwitcher />
          </div>
        </header>

        {viewOnly && (
          <p
            data-testid="view-only-banner"
            className="mx-4 mt-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[13px] text-amber-200"
          >
            Viewing {active.name?.split(" ")[0] || active.email}'s medicines — read only
          </p>
        )}

        <main className="px-4 pt-3">{children}</main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-800 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-md items-stretch pb-1">
          {TABS.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-1 py-2.5 text-xs font-medium transition-colors duration-200 ${
                  isActive ? "text-sky-300" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`flex h-9 w-14 items-center justify-center rounded-full ${
                      isActive ? "bg-sky-500/15 shadow-[0_0_16px_rgba(56,189,248,0.2)]" : ""
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
